/**
 * Comms / Dashboard page at /comms/dashboard.
 *
 * Three independently-loaded sections:
 *   1. Summary strip — outcome stat cards + total line + by-hour bar.
 *   2. Callbacks table — abandoned calls to call back, with the wait time and
 *      the specific place the caller gave up.
 *   3. Voicemail cards — one card per department box.
 *
 * The full call log is a separate tool (Call Log, /comms/call-log). Gated by
 * comms:read (enforced server-side). No back link: this is a top-level tool
 * page. Uses amp-* tokens, shared Pill + DataGridShell components.
 */

import { useState } from 'react';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { DataGridShell } from '@/components/grid/DataGridShell';
import {
  Clock,
  Loader2,
  Phone,
  PhoneMissed,
  PhoneOff,
  Voicemail,
} from 'lucide-react';
import {
  formatDuration,
  formatPhone,
  todayDate,
  useCommsCallbacks,
  useCommsSummary,
  useCommsVoicemail,
  type CommsSummaryHourDest,
  type CommsVoicemailBox,
} from '@/lib/useComms';

// ---- Stat card --------------------------------------------------------------

type StatCardProps = {
  label: string;
  value: string | number;
  tone?: 'green' | 'amber' | 'red' | 'neutral';
  icon?: React.ReactNode;
};

function StatCard({ label, value, tone = 'neutral', icon }: StatCardProps) {
  const valueCls =
    tone === 'green'
      ? 'text-green-700 dark:text-green-400'
      : tone === 'amber'
        ? 'text-amber-700 dark:text-amber-400'
        : tone === 'red'
          ? 'text-red-700 dark:text-red-400'
          : 'text-amp-ink dark:text-amp-dark-ink';
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="flex items-center gap-1 text-[12px] uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {icon && (
          <span aria-hidden className="inline-flex">
            {icon}
          </span>
        )}
        {label}
      </p>
      <p className={`text-2xl font-semibold tabular-nums ${valueCls}`}>{value}</p>
    </div>
  );
}

// ---- By-hour bar strip ------------------------------------------------------

// Categorical palette for the by-destination stack (distinct hues that read in
// light and dark); "Other" is a neutral slate.
const HOUR_PALETTE = ['#83764F', '#2563eb', '#0d9488', '#d97706', '#7c3aed', '#e11d48'];
const HOUR_OTHER = '#94a3b8';
const HOUR_TOP_N = 6;

function HourBar({ byHourDest }: { byHourDest: CommsSummaryHourDest[] }) {
  if (byHourDest.length === 0) return null;

  // Rank destinations by volume; keep the top N, bucket the rest as "Other".
  const totalByReached = new Map<string, number>();
  for (const r of byHourDest) totalByReached.set(r.reached, (totalByReached.get(r.reached) ?? 0) + r.count);
  const ranked = [...totalByReached.entries()].sort((a, b) => b[1] - a[1]);
  const top = ranked.slice(0, HOUR_TOP_N).map(([r]) => r);
  const topSet = new Set(top);
  const cats = ranked.length > HOUR_TOP_N ? [...top, 'Other'] : top;
  const colorFor = (c: string) => (c === 'Other' ? HOUR_OTHER : HOUR_PALETTE[top.indexOf(c) % HOUR_PALETTE.length]);

  const hours = [...new Set(byHourDest.map((r) => r.hour))].sort();
  const perHour = new Map<string, Map<string, number>>();
  for (const h of hours) perHour.set(h, new Map());
  for (const r of byHourDest) {
    const cat = topSet.has(r.reached) ? r.reached : 'Other';
    const m = perHour.get(r.hour)!;
    m.set(cat, (m.get(cat) ?? 0) + r.count);
  }
  const maxTotal = Math.max(1, ...hours.map((h) => [...perHour.get(h)!.values()].reduce((a, b) => a + b, 0)));

  return (
    <div className="rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="mb-3 text-[12px] uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Calls by hour, by destination
      </p>
      <div className="flex h-28 items-end gap-1">
        {hours.map((h) => {
          const m = perHour.get(h)!;
          const hTotal = [...m.values()].reduce((a, b) => a + b, 0);
          const label =
            `${h}:00, ${hTotal} call${hTotal === 1 ? '' : 's'}: ` +
            cats.filter((c) => m.get(c)).map((c) => `${c} ${m.get(c)}`).join(', ');
          return (
            <div
              key={h}
              className="flex h-full min-w-0 flex-1 flex-col items-center gap-1"
              title={label}
              aria-label={label}
            >
              <div className="flex w-full flex-1 flex-col justify-end gap-px overflow-hidden rounded-sm">
                {cats.map((c) => {
                  const cnt = m.get(c) ?? 0;
                  if (!cnt) return null;
                  return (
                    <div
                      key={c}
                      className="w-full"
                      style={{ height: `${(cnt / maxTotal) * 100}%`, backgroundColor: colorFor(c) }}
                    />
                  );
                })}
              </div>
              <span className="text-[10px] tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {h}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
        {cats.map((c) => (
          <span
            key={c}
            className="inline-flex items-center gap-1 text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: colorFor(c) }}
            />
            {c}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---- Summary section --------------------------------------------------------

function SummarySection({ date }: { date: string }) {
  const { summary, isLoading, error } = useCommsSummary(date);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-2 text-sm text-red-700 dark:text-red-300">
        Could not load summary: {error.message}
      </p>
    );
  }

  if (!summary) return null;

  const ratePct = Math.round(summary.abandoned_rate * 100);
  const rateTone: StatCardProps['tone'] = ratePct >= 40 ? 'red' : ratePct >= 20 ? 'amber' : 'neutral';

  return (
    <div className="space-y-3">
      <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="font-semibold tabular-nums text-amp-ink dark:text-amp-dark-ink">
          {summary.total.toLocaleString()}
        </span>{' '}
        total calls
        {summary.after_hours > 0 && (
          <> ({summary.served.toLocaleString()} during open hours, {summary.after_hours.toLocaleString()} after hours)</>
        )}
        . The five outcome cards below add up to the total.
      </p>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard
          label="Answered"
          value={summary.live.toLocaleString()}
          tone="green"
          icon={<Phone className="h-3 w-3" />}
        />
        <StatCard
          label="Voicemail"
          value={summary.voicemail.toLocaleString()}
          tone="neutral"
          icon={<Voicemail className="h-3 w-3" />}
        />
        <StatCard
          label="VM, no message"
          value={summary.no_message.toLocaleString()}
          tone="neutral"
          icon={<Voicemail className="h-3 w-3" />}
        />
        <StatCard
          label="After hours"
          value={summary.after_hours.toLocaleString()}
          tone="neutral"
          icon={<Clock className="h-3 w-3" />}
        />
        <StatCard
          label="Abandoned"
          value={summary.abandoned.toLocaleString()}
          tone={summary.abandoned > 0 ? 'amber' : 'neutral'}
          icon={<PhoneMissed className="h-3 w-3" />}
        />
        <StatCard
          label="Abandoned rate"
          value={`${ratePct}%`}
          tone={rateTone}
          icon={<PhoneOff className="h-3 w-3" />}
        />
      </div>
      <HourBar byHourDest={summary.by_hour_dest} />
    </div>
  );
}

// ---- Callbacks table --------------------------------------------------------

function CallbacksSection({ date }: { date: string }) {
  const { callbacks, isLoading, error } = useCommsCallbacks(date);

  const columns = ['time', 'caller', 'waited', 'gave up at'];

  const body = isLoading ? (
    <tr>
      <td
        colSpan={4}
        className="px-4 py-6 text-center text-amp-ink-muted dark:text-amp-dark-ink-muted"
      >
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Loading callbacks...
      </td>
    </tr>
  ) : error ? (
    <tr>
      <td colSpan={4} className="px-4 py-6 text-center text-red-700 dark:text-red-300">
        Could not load callbacks: {error.message}
      </td>
    </tr>
  ) : callbacks.length === 0 ? (
    <tr>
      <td
        colSpan={4}
        className="px-4 py-6 text-center text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted"
      >
        No abandoned calls today. Everyone reached someone or left a message.
      </td>
    </tr>
  ) : (
    callbacks.map((cb, i) => (
      <tr key={i} className="border-b border-amp-line last:border-0 dark:border-amp-dark-line">
        <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {cb.time}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-xs text-amp-ink dark:text-amp-dark-ink">
          {cb.caller_name ? (
            <div className="flex flex-col">
              <span>{cb.caller_name}</span>
              <span className="font-mono text-[11px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {formatPhone(cb.caller)}
              </span>
            </div>
          ) : (
            <span className="font-mono">{formatPhone(cb.caller)}</span>
          )}
        </td>
        <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {formatDuration(cb.wait_seconds) || '-'}
        </td>
        <td className="px-3 py-2 text-xs">
          <Pill tone="bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
            {cb.reached ?? cb.gave_up_at}
          </Pill>
        </td>
      </tr>
    ))
  );

  return (
    <DataGridShell
      title={
        <span className="inline-flex items-center gap-2">
          <PhoneMissed className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
          Call back
        </span>
      }
      loaded={isLoading ? undefined : callbacks.length}
      countNoun="abandoned calls"
    >
      <table className="min-w-full">
        <thead className={gridTheadClassName}>
          <tr>
            {columns.map((h) => (
              <th key={h} className="px-3 py-2 text-left text-xs font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">{body}</tbody>
      </table>
    </DataGridShell>
  );
}

// ---- Voicemail cards --------------------------------------------------------

function VoicemailCard({ box }: { box: CommsVoicemailBox }) {
  // last_message_at arrives as a plain "YYYY-MM-DD HH:MM" string already in
  // Mountain (PBX) time. Show it as-is; do NOT run it through new Date(), which
  // would reinterpret it as UTC and shift it hours off.
  const lastMsgText = box.last_message_at ?? 'No messages yet';

  // Email-open indicator over the trailing 7 days. Red when emails arrived but
  // none were opened (nobody reading), amber when some, green when all.
  const rec = box.email_received_7d;
  const opn = box.email_opened_7d;
  const openText = rec === 0 ? 'no emails' : `${opn} of ${rec}`;
  const openTone =
    rec === 0
      ? 'text-amp-ink dark:text-amp-dark-ink'
      : opn === 0
        ? 'text-red-700 dark:text-red-400'
        : opn < rec
          ? 'text-amber-700 dark:text-amber-400'
          : 'text-green-700 dark:text-green-400';

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="flex flex-col gap-0.5">
        <p className="text-[14px] font-semibold leading-snug text-amp-ink dark:text-amp-dark-ink">
          {box.label} <span className="font-normal text-amp-ink-muted dark:text-amp-dark-ink-muted">· ext {box.box}</span>
        </p>
        {box.dest_email && (
          <a
            href={`mailto:${box.dest_email}`}
            className="font-mono text-[12px] text-amp-olive hover:underline dark:text-amp-olive-dark"
          >
            {box.dest_email}
          </a>
        )}
      </div>
      <div className="flex gap-4">
        <div>
          <p
            className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted"
            title="Voicemail messages currently sitting in the box (not deleted after emailing)."
          >
            In box
          </p>
          <p className="text-[18px] font-semibold tabular-nums text-amp-ink dark:text-amp-dark-ink">
            {box.unheard_total}
          </p>
        </div>
        <div>
          <p className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">New today</p>
          <p className="text-[18px] font-semibold tabular-nums text-amp-ink dark:text-amp-dark-ink">
            {box.new_today}
          </p>
        </div>
        <div>
          <p
            className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted"
            title="Of the voicemail emails delivered to this box in the last 7 days, how many were opened in Outlook. Opened means the email was opened, not necessarily that the audio was played. Boxes whose email is a distribution list cannot be tracked (no mailbox to read) and show 'no emails'."
          >
            Emails opened (7d)
          </p>
          <p className={`text-[18px] font-semibold tabular-nums ${openTone}`}>{openText}</p>
        </div>
      </div>
      <p className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        Last message: {lastMsgText}
      </p>
    </div>
  );
}

function VoicemailSection() {
  const { boxes, isLoading, error } = useCommsVoicemail();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading voicemail...</span>
      </div>
    );
  }

  if (error) {
    return (
      <p className="py-2 text-sm text-red-700 dark:text-red-300">
        Could not load voicemail: {error.message}
      </p>
    );
  }

  if (boxes.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-amp-line bg-amp-cream-warm px-4 py-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface-warm">
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          No voicemail boxes configured yet.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {boxes.map((box) => (
        <VoicemailCard key={box.box} box={box} />
      ))}
    </div>
  );
}

// ---- Page -------------------------------------------------------------------

export function CommsDashboard() {
  const [date, setDate] = useState<string>(todayDate);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
            Comms
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Dashboard</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Daily call summary, abandoned-call callbacks, and voicemail box status. The full call
            log is under Comms, Call Log.
          </p>
        </div>
        <ToolPinToggle toolSlug="comms-dashboard" toolName="Dashboard" />
      </div>

      {/* Date picker */}
      <div className="flex items-center gap-3">
        <label
          className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted"
          htmlFor="comms-date"
        >
          Date
        </label>
        <input
          id="comms-date"
          type="date"
          value={date}
          max={todayDate()}
          onChange={(e) => {
            if (e.target.value) setDate(e.target.value);
          }}
          className="rounded border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
      </div>

      {/* Summary */}
      <section aria-labelledby="comms-summary-heading">
        <h2 id="comms-summary-heading" className="sr-only">
          Call summary
        </h2>
        <SummarySection date={date} />
      </section>

      {/* Callbacks */}
      <section
        aria-labelledby="comms-callbacks-heading"
        className="flex h-64 min-h-0 flex-col"
      >
        <h2 id="comms-callbacks-heading" className="sr-only">
          Callbacks
        </h2>
        <CallbacksSection date={date} />
      </section>

      {/* Voicemail */}
      <section aria-labelledby="comms-voicemail-heading">
        <h2
          id="comms-voicemail-heading"
          className="mb-3 inline-flex items-center gap-2 text-[16px] font-semibold text-amp-ink dark:text-amp-dark-ink"
        >
          <Voicemail className="h-4 w-4 text-amp-olive dark:text-amp-olive-dark" />
          Voicemail
        </h2>
        <VoicemailSection />
      </section>
    </div>
  );
}
